/**
 * /comps rows through GET /api/comps/rows (RP-5, card x9Qg-r89hVmW): one
 * request per kind replaces that kind's page, its /count, and — for the rows on
 * the page — the reference lists the page joined in the browser (properties,
 * allocations, parties, contacts, parcels). Shapes and the flattening:
 * ./compsRowsData.ts; server side: api src/data/comps-rows.ts.
 *
 * Filters (RP-5b): the applied sidebar filters go as query params
 * (compsRowsFilterParams) and run in the SQL, before the page window; `matched`
 * is what they match (the page count), `total` stays the unfiltered count.
 *
 * Keys: `[<table>, 'list', 'comps-rows', { page, pageSize, filters }]`, so every
 * invalidation of the transaction table (e.g. AddTransactionWizard's
 * `['sale_transactions']`) still refetches the page, as it did the old page
 * query. The rows also carry other tables' data (properties, parties,
 * contacts, parcels), which no table key reaches: main.tsx's MutationCache
 * invalidates `compsRowsKeys.roots` after every successful mutation (the
 * RP-3 / RP-4 pattern for composed reads).
 *
 * `usePropertyDealCounts` reads GET /api/comps/deal-counts for buildings the
 * rows did not count — those only a "Search all DB" result brought onto the
 * page — so the map card's totals stay whole-database counts for them too
 * (the page used to count them over its allocation lists).
 */

import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { httpClient } from '../../http-client';
import { compsRowsFilterParams, type CompsRowsData, type CompsSidebarFilters, type ResolveDateRange } from './compsRowsData';

const TABLE = { sale: 'sale_transactions', lease: 'lease_transactions' } as const;

export const compsRowsKeys = {
  roots: [
    [TABLE.sale, 'list', 'comps-rows'],
    [TABLE.lease, 'list', 'comps-rows'],
    ['comps-deal-counts'],
  ] as const,
  dealCounts: (propertyIds: readonly string[]) => ['comps-deal-counts', propertyIds] as const,
  page: (kind: 'sale' | 'lease', page: number, pageSize: number, filters: ReadonlyArray<[string, string]> = []) =>
    [TABLE[kind], 'list', 'comps-rows', { page, pageSize, filters }] as const,
};

/** The page always asked for the newest first (created_at desc), so a new manual entry lands on page 1. */
export function compsRowsUrl(kind: 'sale' | 'lease', page: number, limit: number, filters: ReadonlyArray<[string, string]> = []): string {
  const params = new URLSearchParams({ kind, page: String(page), limit: String(limit), sort: 'created_at', order: 'desc' });
  for (const [k, v] of filters) params.append(k, v);
  return `/comps/rows?${params.toString()}`;
}

/**
 * One kind's page (of the rows `filters` match — compsRowsFilterParams). While
 * another page loads, `data` is still the previous page's (so its `total` /
 * `matched` keep the page count, and the page number is not clamped back);
 * `rows` is empty and `isLoading` true meanwhile, as the old per-page query
 * showed its spinner.
 */
export function useCompsRows(kind: 'sale' | 'lease', page: number, pageSize: number, filters: ReadonlyArray<[string, string]> = []) {
  const q = useQuery<CompsRowsData>({
    queryKey: compsRowsKeys.page(kind, page, pageSize, filters),
    queryFn: async () => (await httpClient.get<{ data: CompsRowsData }>(compsRowsUrl(kind, page, pageSize, filters))).data,
    placeholderData: keepPreviousData,
  });
  const current = q.isPlaceholderData ? undefined : q.data;
  return {
    data: current,
    total: q.data?.total ?? 0,
    matched: q.data?.matched ?? 0,
    isLoading: q.isLoading || q.isPlaceholderData,
  };
}

/** The inactive tab's request carries no sidebar filters. */
const NO_FILTERS: Array<[string, string]> = [];

/**
 * The /comps page's two requests (RP-5b): the applied sidebar filters run in
 * the SQL for the active tab's kind, before the page window; the other kind's
 * page stays unfiltered, as it was loaded before (cart / selection). New
 * filters start again at page 1 — reset while rendering, so the stale page is
 * never requested with them. `serverRowIds`: the active kind's rows the server
 * filtered (only "Search all DB" rows still need the page's own filter pass).
 * `archived` (D2-S S7, archivers only — ArchivedViewPill's archivedParam): the
 * Archived view, sent with BOTH kinds so the tab counts follow it; absent, the
 * requests are exactly as before.
 */
export function useCompsPageRows<P extends string>(
  active: 'sale' | 'lease',
  filters: CompsSidebarFilters<P>,
  resolveDateRange: ResolveDateRange<P>,
  page: number,
  setPage: (page: number) => void,
  pageSize: number,
  archived?: 'only' | 'include',
) {
  const filterParams = useMemo(() => compsRowsFilterParams(filters, resolveDateRange), [filters, resolveDateRange]);
  const params = useMemo<Array<[string, string]>>(
    () => (archived ? [...filterParams, ['archived', archived]] : filterParams),
    [filterParams, archived],
  );
  const otherParams = useMemo<Array<[string, string]>>(() => (archived ? [['archived', archived]] : NO_FILTERS), [archived]);
  const key = useMemo(() => JSON.stringify(params), [params]);
  const [pageKey, setPageKey] = useState(key);
  if (pageKey !== key) {
    setPageKey(key);
    setPage(1);
  }
  const sale = useCompsRows('sale', page, pageSize, active === 'sale' ? params : otherParams);
  const lease = useCompsRows('lease', page, pageSize, active === 'lease' ? params : otherParams);
  const activeData = active === 'sale' ? sale.data : lease.data;
  const serverRowIds = useMemo(() => new Set<string>((activeData?.rows ?? []).map((r) => r.transaction?.id)), [activeData]);
  return { sale, lease, serverRowIds };
}

/** The server's limit per request; more ids go out as several requests in one fetch. */
const DEAL_COUNTS_CHUNK = 200;

/** Per-building deal counts for `propertyIds` (sorted, distinct), only while `enabled`. */
export function usePropertyDealCounts(propertyIds: readonly string[], enabled: boolean) {
  const q = useQuery<Record<string, { sales: number; leases: number }>>({
    queryKey: compsRowsKeys.dealCounts(propertyIds),
    enabled: enabled && propertyIds.length > 0,
    queryFn: async () => {
      const chunks: string[][] = [];
      for (let i = 0; i < propertyIds.length; i += DEAL_COUNTS_CHUNK) chunks.push(propertyIds.slice(i, i + DEAL_COUNTS_CHUNK));
      const parts = await Promise.all(chunks.map((ids) =>
        httpClient.get<{ data: Record<string, { sales: number; leases: number }> }>(`/comps/deal-counts?propertyIds=${ids.join(',')}`),
      ));
      return Object.assign({}, ...parts.map((p) => p.data));
    },
  });
  return q.data;
}
